import {
  Button,
  CheckInput,
  ColumnTable,
  DisplayInfo,
  Icon,
  Loading,
  Table,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DisplayError from '../../../../components/info/DisplayError';
import Tooltip from '../../../../components/tooltip/Tooltip';
import { TranslationsKeys } from '../../../../locales/translations';
import { usePaymentHubDebtsLoader } from '../loaders/PaymentHubDebtsData';
import { PaymentHubDebt, PaymentHubDebtStatus } from '../types';
import RegisterPaymentModal, {
  RegisterPaymentModalMethods,
} from './RegisterPaymentModal';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const IdentificationTable = () => {
  const { t } = useTranslation(TranslationsKeys.PAYMENT_HUB_DEBTS);
  const registerPaymentModalRef = useRef<RegisterPaymentModalMethods>(null);
  const { query, result, loading, refresh } = usePaymentHubDebtsLoader();

  const [selectedDebts, setSelectedDebts] = useState<unknown[]>([]);

  const items = useMemo(() => {
    if (!result?.success) {
      return [];
    }
    return result.data ?? [];
  }, [result]);

  const allowedItems = useMemo(() => {
    return items.filter((item) => item.status === PaymentHubDebtStatus.Pending);
  }, [items]);

  const handleCreatePayment = useCallback(
    (row: PaymentHubDebt) => {
      if (registerPaymentModalRef.current) {
        registerPaymentModalRef.current.open([row.id], query);
      }
    },
    [query],
  );

  const handleCreatePaymentMultiple = useCallback(
    (rows: PaymentHubDebt['id'][]) => {
      if (registerPaymentModalRef.current) {
        registerPaymentModalRef.current.open(rows, query);
      }
    },
    [query],
  );

  const handleSelectSingleDebt = useCallback(
    (checked: boolean, row: PaymentHubDebt) => {
      setSelectedDebts((prev) => {
        if (checked) {
          return [...prev, row.id];
        } else {
          return prev.filter((id) => id !== row.id);
        }
      });
    },
    [],
  );

  const handleSelectAllDebts = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedDebts(allowedItems.map((item) => item.id));
      } else {
        setSelectedDebts([]);
      }
    },
    [allowedItems],
  );

  const columns = useMemo<ColumnTable<PaymentHubDebt>[]>(
    () => [
      {
        columnName: 'select',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: (
          <div>
            <CheckInput
              value={
                selectedDebts.length === allowedItems?.length &&
                allowedItems?.length > 0
              }
              disabled={allowedItems?.length === 0}
              label=""
              name="select_all_debts"
              onChange={(e) => handleSelectAllDebts(e.target.checked)}
            />
          </div>
        ),
        width: 50,
        cellFormat: ({ row }) => {
          return (
            <div>
              <CheckInput
                value={selectedDebts.includes(row.id)}
                label=""
                name={`select_debt_${row.id}`}
                disabled={row.status !== PaymentHubDebtStatus.Pending}
                onChange={(e) => handleSelectSingleDebt(e.target.checked, row)}
              />
            </div>
          );
        },
      },
      {
        columnName: 'idToDisplay',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.idToDisplay`),
      },
      {
        columnName: 'type',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.type`),
      },
      {
        columnName: 'details',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.details`),
      },
      {
        columnName: 'installment',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.installment`),
      },
      {
        columnName: 'installmentDueDate',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.installmentDueDate`),
        cellFormat: ({ value }) => {
          if (!value) {
            return '-';
          }
          return dayjs.utc(value).format('DD/MM/YYYY');
        },
      },
      {
        columnName: 'amount',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.amount`),
        cellFormat: ({ row }) => {
          return row?.amount?.value?.toLocaleString('es-CL', {
            style: 'currency',
            currency: row?.amount?.currency ?? 'CLP',
          });
        },
      },
      {
        columnName: 'interests',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.interests`),
        cellFormat: ({ row }) => {
          return row?.interests?.value?.toLocaleString('es-CL', {
            style: 'currency',
            currency: row?.interests?.currency ?? 'CLP',
          });
        },
      },
      {
        columnName: 'finalAmount',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.finalAmount`),
        cellFormat: ({ row }) => {
          return (
            <strong>
              {row?.finalAmount?.value?.toLocaleString('es-CL', {
                style: 'currency',
                currency: row?.finalAmount?.currency ?? 'CLP',
              })}
            </strong>
          );
        },
      },
      {
        columnName: 'status',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.status`),
        width: 120,
        cellFormat: ({ value, row }) => {
          if (value === PaymentHubDebtStatus.InProcess) {
            return (
              <Tooltip
                id={`debt_${row.id}`}
                placement="bottom"
                text={t(`table.tooltips.in_process`)}
              >
                <Icon name="circle" />
              </Tooltip>
            );
          }
          if (value === PaymentHubDebtStatus.Payed) {
            return (
              <Tooltip
                id={`debt_${row.id}`}
                placement="bottom"
                text={t(`table.tooltips.payed`)}
              >
                <Icon name="success" />
              </Tooltip>
            );
          }
          return '';
        },
      },
      {
        columnName: 'id',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: '',
        width: 180,
        cellFormat: ({ row }) => {
          return (
            <Button
              size="sm"
              text={t(`table.actions.registerPresentialPayment`)}
              disabled={row.status !== PaymentHubDebtStatus.Pending}
              onClick={() => handleCreatePayment(row)}
            />
          );
        },
      },
    ],
    [
      selectedDebts,
      allowedItems?.length,
      t,
      handleCreatePayment,
      handleSelectSingleDebt,
      handleSelectAllDebts,
    ],
  );

  const selectedAmount = useMemo(() => {
    return items
      .filter((item) => selectedDebts.includes(item.id))
      .reduce((sum, debt) => sum + debt.finalAmount.value, 0);
  }, [items, selectedDebts]);

  useEffect(() => {
    setSelectedDebts([]);
  }, [items]);

  if (loading) {
    return <Loading textBody="" />;
  }

  if (
    !query?.identificationDocument ||
    !query?.identificationDocumentType ||
    !query?.identificationDocumentCountry
  ) {
    return (
      <DisplayInfo
        title={t('noFiltersSelected.title')}
        textBody={t('noFiltersSelected.description')}
      />
    );
  }

  if (!result?.success) {
    return (
      <DisplayError
        title={t('queryErrors.title')}
        textBody={t('queryErrors.description')}
      />
    );
  }

  return (
    <div className="p-5">
      <div
        className="w-full d-flex align-items-end mb-3"
        style={{ gap: 5, minHeight: 75 }}
      >
        <p className="mb-0 flex-fill">{t('tablePrefix')}</p>
        {selectedAmount > 0 && (
          <div
            className="d-flex justify-content-center flex-column"
            style={{ maxWidth: 280, gap: 5 }}
          >
            <Button
              text={t('table.actions.registerPresentialPaymentMultiple')}
              size="sm"
              onClick={() => handleCreatePaymentMultiple(selectedDebts)}
            />
            <strong className="w-full text-center">
              {t('totalAmount', {
                amount: selectedAmount.toLocaleString('es-CL', {
                  style: 'currency',
                  currency: 'CLP',
                }),
              })}
            </strong>
          </div>
        )}
      </div>
      <Table
        columns={columns}
        data={items}
        noResultsText={t(`empty.description`)}
      />
      <RegisterPaymentModal
        ref={registerPaymentModalRef}
        onSave={() => {
          refresh();
          setSelectedDebts([]);
        }}
      />
    </div>
  );
};

export default IdentificationTable;
